import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAppConfig } from '../../config/AppConfigContext';
import type { Conversation } from '../../types/voice-demo';

const joinUrl = (baseUrl: string, path: string) => {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

const fetchConversations = async (
  baseUrl: string,
  token: string,
  userId: string,
  companyId: string,
  conversationId?: string,
): Promise<Conversation | null> => {
  const params = new URLSearchParams({ companyId });
  if (conversationId) params.set('conversationId', conversationId);

  const response = await fetch(
    joinUrl(baseUrl, `api/v1/partner/users/${encodeURIComponent(userId)}/conversations?${params}`),
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store',
    },
  );

  if (response.status === 404) return null;

  if (!response.ok) {
    const responseText = await response.text().catch(() => '');
    throw new Error(
      `Failed to fetch conversations: ${response.status} ${response.statusText}${responseText ? ` - ${responseText}` : ''}`,
    );
  }

  return response.json() as Promise<Conversation>;
};

export const useConversationsQuery = (
  userId: string | null,
  companyId: string | null,
  conversationId?: string,
): UseQueryResult<Conversation | null, Error> => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();

  return useQuery({
    queryKey: ['partner', 'users', userId, 'conversations', conversationId ?? 'latest'],
    enabled: Boolean(isAuthenticated && userId && companyId),
    queryFn: async () => {
      if (!allyApi || !userId || !companyId) {
        throw new Error('Missing required configuration');
      }

      const token = auth0Audience
        ? await getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
        : await getAccessTokenSilently();

      return fetchConversations(allyApi, token, userId, companyId, conversationId);
    },
  });
};
